/**
 * Handwritten TanStack Query hooks wrapping the generated SDK.
 *
 * Pattern:
 *   - One query/mutation hook per endpoint.
 *   - `queryKey` is the hook's stable identifier. Mutations invalidate the
 *     keys whose data they make stale.
 *   - All hooks return the standard TanStack Query result, so consumers can
 *     destructure `{ data, isLoading, error, mutate, ... }` as usual.
 *
 * To add a hook for a new endpoint, follow the existing examples — copy a
 * useQuery wrapper for a GET, a useMutation wrapper for a POST/PUT/DELETE,
 * and call `queryClient.invalidateQueries` for any keys the mutation
 * affects.
 */

import { type QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  adminActivityActions,
  adminActivityAnalytics,
  adminDeleteUser,
  adminGetUser,
  adminInviteUser,
  adminListActivity,
  adminListUsers,
  adminUpdateUser,
  changePassword,
  createConversation,
  createDashboard,
  createPin,
  deleteConversation,
  deleteDashboard,
  deletePin,
  getActivity,
  getAuthConfig,
  getConversation,
  getHealth,
  getMe,
  getMeDetail,
  getMyVote,
  getVoteSummary,
  getVoteThemes,
  listConversations,
  listDashboards,
  listPins,
  login,
  logout,
  type PinLayoutItem,
  resetVotes,
  submitVote,
  updateDashboard,
  updatePinLayout,
  updateProfile,
  type VoteSummaryResponse,
  type VoteThemesResponse,
} from "@/lib/api/generated";

// ─── Public queries / mutations ──────────────────────────────────────────

export function useHealth() {
  return useQuery({
    queryKey: ["health"],
    queryFn: async () => {
      const { data } = await getHealth();
      return data;
    },
  });
}

export function useAuthConfig() {
  return useQuery({
    queryKey: ["auth", "config"],
    queryFn: async () => {
      const { data } = await getAuthConfig();
      return data;
    },
    // The provider is fixed at deploy time — never refetch it.
    staleTime: Infinity,
  });
}

export function useMe() {
  return useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      const { data } = await getMe();
      return data;
    },
    // Don't retry on a 401 — ProtectedRoute will redirect to /login.
    retry: false,
  });
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { email: string; password: string; rememberMe?: boolean }) => {
      const { data } = await login({
        body: {
          email: vars.email,
          password: vars.password,
          remember_me: vars.rememberMe ?? false,
        },
      });
      return data;
    },
    onSuccess: () => {
      // A login can follow a session that ended WITHOUT the in-app logout
      // (expired/cleared cookie), so the cache may still hold the previous
      // identity's data — and a different user logging in on the same tab
      // would briefly see it. Clear everything, mirroring useLogout; the new
      // session refetches what it needs (including "me").
      queryClient.clear();
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await logout();
    },
    onSuccess: () => {
      // Clear the entire cache so no stale per-user data leaks across sessions.
      queryClient.clear();
    },
  });
}

// ─── Profile ─────────────────────────────────────────────────────────────

export function useMeDetail() {
  return useQuery({
    queryKey: ["me", "detail"],
    queryFn: async () => {
      const { data } = await getMeDetail();
      return data;
    },
    retry: false,
  });
}

export function useActivity() {
  return useQuery({
    queryKey: ["activity"],
    queryFn: async () => {
      const { data } = await getActivity();
      return data ?? [];
    },
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { firstName: string; lastName: string }) => {
      await updateProfile({
        body: { first_name: vars.firstName, last_name: vars.lastName },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: async (vars: { currentPassword: string; newPassword: string }) => {
      await changePassword({
        body: { current_password: vars.currentPassword, new_password: vars.newPassword },
      });
    },
  });
}

// ─── Admin ───────────────────────────────────────────────────────────────

export function useAdminUsers() {
  return useQuery({
    queryKey: ["admin", "users"],
    queryFn: async () => {
      const { data } = await adminListUsers();
      return data ?? [];
    },
  });
}

export function useAdminUser(userId: string | null) {
  return useQuery({
    queryKey: ["admin", "users", userId],
    queryFn: async () => {
      if (!userId) {
        return null;
      }
      const { data } = await adminGetUser({ path: { user_id: userId } });
      return data;
    },
    enabled: !!userId,
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      userId: string;
      firstName?: string;
      lastName?: string;
      role?: string;
    }) => {
      await adminUpdateUser({
        path: { user_id: vars.userId },
        body: {
          first_name: vars.firstName,
          last_name: vars.lastName,
          role: vars.role,
        },
      });
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "users", vars.userId] });
    },
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { userId: string }) => {
      await adminDeleteUser({ path: { user_id: vars.userId } });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });
}

export function useInviteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { email: string }) => {
      const { data } = await adminInviteUser({ body: { email: vars.email } });
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });
}

// ─── Admin activity ──────────────────────────────────────────────────────

export interface AdminActivityParams {
  search?: string;
  action?: string;
  userId?: string;
  sortBy?: string;
  sortDir?: "asc" | "desc";
  page?: number;
  pageSize?: number;
}

export function useAdminActivity(params: AdminActivityParams = {}) {
  return useQuery({
    queryKey: ["admin", "activity", params],
    queryFn: async () => {
      const { data } = await adminListActivity({
        query: {
          search: params.search,
          action: params.action,
          user_id: params.userId,
          sort_by: params.sortBy ?? "created_at",
          sort_dir: params.sortDir ?? "desc",
          page: params.page ?? 1,
          page_size: params.pageSize ?? 25,
        },
      });
      return data;
    },
    // Keep the previous page on screen while fetching the next so the table
    // doesn't flash empty between paginations / filter changes.
    placeholderData: (prev) => prev,
  });
}

export function useAdminActivityAnalytics(days = 30) {
  return useQuery({
    queryKey: ["admin", "activity", "analytics", days],
    queryFn: async () => {
      const { data } = await adminActivityAnalytics({ query: { days } });
      return data;
    },
  });
}

export function useAdminActivityActions() {
  return useQuery({
    queryKey: ["admin", "activity", "actions"],
    queryFn: async () => {
      const { data } = await adminActivityActions();
      return data ?? [];
    },
  });
}

// ─── Agent: conversations ────────────────────────────────────────────────

/** Query keys other modules (the chat page, `streamAgentMessage` callers) invalidate by hand.
 *  `pins` takes an optional board id so a board-scoped fetch (the Dashboard page) and the
 *  unscoped "every pin, any board" fetch (the chat rail's pin-status lookups) cache separately —
 *  invalidating with the `["agent", "pins"]` prefix hits both at once. */
export const agentKeys = {
  conversations: ["agent", "conversations"] as const,
  conversation: (id: string) => ["agent", "conversations", id] as const,
  dashboards: ["agent", "dashboards"] as const,
  pins: (dashboardId?: string | null) => ["agent", "pins", dashboardId ?? null] as const,
};

/** Warms the Dashboard page's first fetch during the login round-trip — the same idea as
 *  `prefetchFieldData`, aimed at the agent-native landing page instead of the field snapshot. */
export function prefetchDashboardData(queryClient: QueryClient): void {
  void queryClient.prefetchQuery({
    queryKey: agentKeys.dashboards,
    queryFn: async () => {
      const { data } = await listDashboards();
      return data ?? [];
    },
  });
}

export function useConversations() {
  return useQuery({
    queryKey: agentKeys.conversations,
    queryFn: async () => {
      const { data } = await listConversations();
      return data ?? [];
    },
  });
}

export function useConversation(conversationId: string | null) {
  return useQuery({
    queryKey: agentKeys.conversation(conversationId ?? ""),
    queryFn: async () => {
      if (!conversationId) {
        return null;
      }
      const { data } = await getConversation({ path: { conversation_id: conversationId } });
      return data;
    },
    enabled: !!conversationId,
  });
}

export function useCreateConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { title?: string } = {}) => {
      const { data } = await createConversation({ body: { title: vars.title ?? null } });
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: agentKeys.conversations });
    },
  });
}

export function useDeleteConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { conversationId: string }) => {
      await deleteConversation({ path: { conversation_id: vars.conversationId } });
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: agentKeys.conversations });
      queryClient.removeQueries({ queryKey: agentKeys.conversation(vars.conversationId) });
    },
  });
}

// ─── Agent: dashboards ───────────────────────────────────────────────────

/** Every user has at least one board; the backend creates the default and never returns an
 *  empty list, so callers can render tabs straight off `.data` without an empty-state branch. */
export function useDashboards() {
  return useQuery({
    queryKey: agentKeys.dashboards,
    queryFn: async () => {
      const { data } = await listDashboards();
      return data ?? [];
    },
  });
}

export function useCreateDashboard() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { name?: string } = {}) => {
      const { data } = await createDashboard({ body: { name: vars.name ?? null } });
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: agentKeys.dashboards });
    },
  });
}

export function useUpdateDashboard() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { dashboardId: string; name?: string; position?: number }) => {
      const { data } = await updateDashboard({
        path: { dashboard_id: vars.dashboardId },
        body: { name: vars.name, position: vars.position },
      });
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: agentKeys.dashboards });
    },
  });
}

/** The backend refuses (409) to delete a user's last board — callers should catch that via the
 *  `ApiError.status` shape and show a friendly inline message rather than a generic failure. */
export function useDeleteDashboard() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { dashboardId: string }) => {
      await deleteDashboard({ path: { dashboard_id: vars.dashboardId } });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: agentKeys.dashboards });
      // Deleting a board deletes its pins — every pin query (board-scoped or not) is stale.
      void queryClient.invalidateQueries({ queryKey: ["agent", "pins"] });
    },
  });
}

// ─── Agent: pins ─────────────────────────────────────────────────────────

/** All of the user's pins across every board — what the chat rail needs to know a view's
 *  pinned-elsewhere state without fetching per-board. Pass a `dashboardId` to scope the fetch to
 *  one board's pins instead (what the Dashboard page renders as cards). */
export function usePins(dashboardId?: string | null) {
  return useQuery({
    queryKey: agentKeys.pins(dashboardId),
    queryFn: async () => {
      const { data } = await listPins({ query: { dashboard_id: dashboardId ?? null } });
      return data ?? [];
    },
  });
}

/** Dashboard-page variant of `usePins`, scoped to one board and disabled until that board is
 *  known — the caller resolves the active tab (from `useDashboards` + localStorage) before this
 *  can fire, so it never fetches with the wrong (or no) `dashboardId`. */
export function useDashboardPins(dashboardId: string | null) {
  return useQuery({
    queryKey: agentKeys.pins(dashboardId),
    queryFn: async () => {
      const { data } = await listPins({ query: { dashboard_id: dashboardId } });
      return data ?? [];
    },
    enabled: !!dashboardId,
  });
}

export function useCreatePin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      viewId: string;
      dashboardId?: string | null;
      conversationId?: string | null;
    }) => {
      const { data } = await createPin({
        body: { view_id: vars.viewId, dashboard_id: vars.dashboardId ?? null },
      });
      return data;
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: ["agent", "pins"] });
      // The rail shows `pinned_dashboard_ids` from the view's own conversation query — that's
      // the only thing that tells it this pin landed, so it has to go stale too.
      if (vars.conversationId) {
        void queryClient.invalidateQueries({
          queryKey: agentKeys.conversation(vars.conversationId),
        });
      }
    },
  });
}

export function useDeletePin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { pinId: string; conversationId?: string | null }) => {
      await deletePin({ path: { pin_id: vars.pinId } });
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: ["agent", "pins"] });
      if (vars.conversationId) {
        void queryClient.invalidateQueries({
          queryKey: agentKeys.conversation(vars.conversationId),
        });
      }
    },
  });
}

/** Persists the whole board after a drag/resize settles. */
export function useUpdatePinLayout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { dashboardId: string; items: PinLayoutItem[] }) => {
      const { data } = await updatePinLayout({ body: { items: vars.items } });
      return data ?? [];
    },
    onSuccess: (data, vars) => {
      // The endpoint answers with every pin the user owns, not just this board's — filter down
      // to the board being saved before writing the cache, so its scoped query never ends up
      // holding cards that belong on a different tab.
      const board = data.filter((pin) => pin.dashboard_id === vars.dashboardId);
      queryClient.setQueryData(agentKeys.pins(vars.dashboardId), board);
    },
  });
}

// ─── Audience voting ─────────────────────────────────────────────────────

/** The unauthenticated /vote page's own vote, resolved from the `boi-vote-id` cookie the
 *  backend sets — no auth required, so this is safe to call before `useMe` resolves. */
export function useMyVote() {
  return useQuery({
    queryKey: ["votes", "me"],
    queryFn: async () => {
      const { data } = await getMyVote();
      return data;
    },
    retry: false,
  });
}

/**
 * Submit or edit a vote. Re-submitting under the same cookie edits in place, so this
 * invalidates both the attendee's own vote and the presenter summary.
 */
export function useSubmitVote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { score: number; pros: string[]; cons: string[] }) => {
      const { data } = await submitVote({
        body: { score: vars.score, pros: vars.pros, cons: vars.cons },
      });
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["votes", "me"] });
      void queryClient.invalidateQueries({ queryKey: ["votes", "summary"] });
    },
  });
}

/**
 * Presenter-side score distribution. Cheap and poll-safe — the dialog passes
 * `refetchInterval` (~10s) while it is open so the live tally keeps ticking.
 */
export function useVoteSummary(opts: { enabled?: boolean; refetchInterval?: number } = {}) {
  return useQuery({
    queryKey: ["votes", "summary"],
    queryFn: async () => {
      const { data } = await getVoteSummary();
      return data;
    },
    enabled: opts.enabled ?? true,
    refetchInterval: opts.refetchInterval,
  });
}

/**
 * LLM-clustered pros/cons for the revealed results panel. `enabled` gates it to the revealed
 * state; `refetchInterval` (~10s) keeps it live — no manual refresh needed.
 */
export function useVoteThemes(opts: { enabled?: boolean; refetchInterval?: number } = {}) {
  return useQuery({
    queryKey: ["votes", "themes"],
    queryFn: async () => {
      const { data } = await getVoteThemes();
      return data;
    },
    enabled: opts.enabled ?? false,
    refetchInterval: opts.refetchInterval,
    refetchOnWindowFocus: false,
  });
}

/**
 * Clear every vote and the server-side theme cache — lets a presenter reset between a
 * rehearsal and the live demo. Paints the wiped state directly (themes only mounts on the
 * revealed results view, so invalidating alone wouldn't refetch it while disabled) and
 * invalidates both queries as a safety net for whenever they're enabled.
 */
export function useResetVotes() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await resetVotes();
      return data;
    },
    onSuccess: () => {
      queryClient.setQueryData<VoteThemesResponse>(["votes", "themes"], {
        pros_clusters: [],
        cons_clusters: [],
        based_on_total: 0,
      });
      queryClient.setQueryData<VoteSummaryResponse>(["votes", "summary"], {
        total: 0,
        average: null,
        distribution: [1, 2, 3, 4, 5].map((score) => ({ score, count: 0 })),
      });
      void queryClient.invalidateQueries({ queryKey: ["votes", "summary"] });
      void queryClient.invalidateQueries({ queryKey: ["votes", "themes"] });
    },
  });
}
